import type { Sku } from '@commercelayer/sdk'

/**
 * A composable for managing the state of a gift detail side slide.
 *
 * Provides reactive state management for displaying gift details
 * in a side slide/panel component.
 *
 * @returns {Object} An object containing the gift detail state and a setter method
 *
 * @example
 * const { giftDetailSideSlideState, setGiftDetailSideSlideState } = useGiftDetailSideSlide()
 *
 * // Set a gift detail
 * setGiftDetailSideSlideState(giftSku)
 *
 * // Access current gift detail
 * const currentGift = giftDetailSideSlideState.value
 *
 * @remarks
 * - Uses Nuxt's useState for reactive state management
 * - Supports setting and clearing gift details
 * - Typed with Commerce Layer Sku type
 */
export const useGiftDetailSideSlide = () => {
  /**
   * Reactive state for storing the current gift detail SKU.
   * @type {Ref<Sku | undefined>}
   */
  const giftDetailSideSlideState = useState<Sku | undefined>(
    'gift-detail-side-slide-status'
  )

  /**
   * Sets the current gift detail state.
   *
   * @param {Sku | undefined} props - The gift SKU to set, or undefined to clear
   */
  const setGiftDetailSideSlideState = (props: Sku | undefined) => {
    giftDetailSideSlideState.value = props
  }

  return {
    giftDetailSideSlideState,
    setGiftDetailSideSlideState,
  }
}
